import PlusOutlined from "@ant-design/icons/PlusOutlined"
import { Flex, Input, InputRef, Tag, Tooltip } from "antd"

import TextArea from "antd/es/input/TextArea"
import { useEffect, useRef, useState } from "react"
import { GeneralInformationData } from "~/types/types"
import { t } from "../../../languages/i18n"
const tagInputStyle: React.CSSProperties = {
  width: 64,
  height: 22,
  marginInlineEnd: 8,
  verticalAlign: "top",
}

interface GeneralInformationProps {
  generalInfo: GeneralInformationData
  setGeneralInfo: (data: { tags: string[]; websiteName: string; websiteDescription: string }) => void
}

const GeneralInformation: React.FC<GeneralInformationProps> = ({ setGeneralInfo, generalInfo }) => {
  const [tags, setKeywords] = useState<string[]>(generalInfo.tags || [])
  const [inputVisible, setInputVisible] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const [editInputIndex, setEditInputIndex] = useState(-1)
  const [editInputValue, setEditInputValue] = useState("")
  const inputRef = useRef<InputRef>(null)
  const editInputRef = useRef<InputRef>(null)

  const [websiteName, setWebsiteName] = useState(generalInfo.websiteName || "")
  const [websiteDescription, setWebsiteDescription] = useState(generalInfo.websiteDescription || "")

  useEffect(() => {
    if (inputVisible) {
      inputRef.current?.focus()
    }
  }, [inputVisible])

  useEffect(() => {
    setWebsiteName(generalInfo.websiteName)
    setWebsiteDescription(generalInfo.websiteDescription)
    setKeywords(generalInfo.tags || [])
  }, [generalInfo])

  useEffect(() => {
    editInputRef.current?.focus()
  }, [editInputValue])
  const handleClose = (removedTag: string) => {
    const newTags = tags.filter(tag => tag !== removedTag)
    setKeywords(newTags)
    setGeneralInfo({ tags: newTags, websiteName, websiteDescription })
  }

  const showInput = () => {
    setInputVisible(true)
  }
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
  }

  const handleInputConfirm = () => {
    if (inputValue && !tags.includes(inputValue)) {
      const newTags = [...tags, inputValue]
      setKeywords(newTags)
      setGeneralInfo({ tags: newTags, websiteName, websiteDescription })
    }
    setInputVisible(false)
    setInputValue("")
  }

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditInputValue(e.target.value)
  }
  const handleEditInputConfirm = () => {
    const newTags = [...tags]
    newTags[editInputIndex] = editInputValue
    setKeywords(newTags)
    setGeneralInfo({ tags: newTags, websiteName, websiteDescription })
    setEditInputIndex(-1)
    setEditInputValue("")
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWebsiteName(e.target.value)
    setGeneralInfo({ tags, websiteName: e.target.value, websiteDescription })
  }

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setWebsiteDescription(e.target.value)
    setGeneralInfo({ tags, websiteName, websiteDescription: e.target.value })
  }

  return (
    <div className="w-full flex flex-row gap-4 border-b pb-8">
      <div className="w-2/5 flex flex-col gap-2 py-4">
        <h2 className="text-base font-semibold">{t("General information of the website")}</h2>
        <p>{t("Description and keyword tags help determine your website will appear on search engines")}</p>
      </div>
      <div className=" w-3/5 flex flex-col gap-2 bg-white drop-shadow-md p-4 rounded-lg">
        <div className="w-full flex flex-col gap-2">
          <h2>{t("Website name")}</h2>
          <Input size="large" defaultValue={websiteName} value={websiteName} onChange={handleNameChange} />
        </div>
        <div className="w-full flex flex-col gap-2 ">
          <h2>{t("Keywords")}</h2>
          <Flex gap="4px 0" wrap>
            {tags.map<React.ReactNode>((tag, index) => {
              if (editInputIndex === index) {
                return (
                  <Input
                    ref={editInputRef}
                    key={tag}
                    size="small"
                    style={tagInputStyle}
                    value={editInputValue}
                    onChange={handleEditInputChange}
                    onBlur={handleEditInputConfirm}
                    onPressEnter={handleEditInputConfirm}
                    defaultValue={tags}
                  />
                )
              }
              const isLongTag = tag.length > 20
              const tagElem = (
                <Tag key={tag} closable={index !== 0} style={{ userSelect: "none" }} onClose={() => handleClose(tag)}>
                  <span
                    onDoubleClick={e => {
                      if (index !== 0) {
                        setEditInputIndex(index)
                        setEditInputValue(tag)
                        e.preventDefault()
                      }
                    }}
                  >
                    {isLongTag ? `${tag.slice(0, 20)}...` : tag}
                  </span>
                </Tag>
              )
              return isLongTag ? (
                <Tooltip title={tag} key={tag}>
                  {tagElem}
                </Tooltip>
              ) : (
                tagElem
              )
            })}
            {inputVisible ? (
              <Input
                ref={inputRef}
                type="text"
                size="small"
                style={tagInputStyle}
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputConfirm}
                onPressEnter={handleInputConfirm}
              />
            ) : (
              <Tag icon={<PlusOutlined />} onClick={showInput}>
                {t("Add new keyword")}
              </Tag>
            )}
          </Flex>
        </div>
        <div className="w-full flex flex-col gap-2 mt-4">
          <h2>{t("Website description")}</h2>
          <TextArea
            placeholder={t("Website description")}
            autoSize={{ minRows: 3, maxRows: 6 }}
            value={websiteDescription}
            onChange={handleDescriptionChange}
            defaultValue={websiteDescription}
          />
        </div>
      </div>
    </div>
  )
}

export default GeneralInformation
